import React from "react";
import { Link } from "react-router-dom";
import { BRAND, SERVICES } from "../lib/brand";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo, { breadcrumbLd } from "../components/Seo";
import { Phone, ArrowRight, ShieldCheck } from "lucide-react";

export default function ServicesIndex() {
  return (
    <div data-testid="services-index-page" className="min-h-screen bg-white overflow-x-hidden">
      <Seo
        title="Our Services - Pest & Termite Control South Florida"
        description="Explore Dead Bug Edwards' full range of pest control services: termite tenting, general pest control, rodent, and marine - all backed by our 65-year guarantee."
        canonical="/services"
        jsonLd={[
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            itemListElement: SERVICES.map((s, i) => ({
              "@type": "ListItem",
              position: i + 1,
              url: `https://dbe.bp.agency/services/${s.slug}`,
              name: s.title,
            })),
          },
        ]}
      />
      <Header />

      {/* Hero */}
      <section className="relative pt-[110px] md:pt-[140px] pb-14 md:pb-20 bg-gradient-to-b from-[color:var(--dbe-cream)] to-white overflow-hidden">
        <div className="absolute -top-24 -right-24 w-[500px] h-[500px] rounded-full bg-[color:var(--dbe-red)]/5 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-24 -left-24 w-[420px] h-[420px] rounded-full bg-[color:var(--dbe-gold)]/10 blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl mx-auto px-5 sm:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-[color:var(--dbe-red)]/10 text-[color:var(--dbe-red)] rounded-full px-4 py-1.5 text-xs font-bold tracking-[0.25em] uppercase">
            <ShieldCheck className="w-4 h-4" /> Since {BRAND.since}
          </div>
          <h1
            data-testid="services-index-headline"
            className="mt-5 font-bebas leading-[0.95] text-[clamp(2.75rem,7vw,6rem)] text-neutral-900"
          >
            Our <span className="text-[color:var(--dbe-red)]">Services</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg md:text-xl text-neutral-700 max-w-3xl mx-auto leading-relaxed">
            From termites to rodents to marine vessels - if it bugs you, we handle it.
            Every plan is tailored to your property, season and pest pressure.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/#quote"
              data-testid="services-index-cta-quote"
              className="inline-flex items-center justify-center gap-2 bg-[color:var(--dbe-red)] hover:bg-[color:var(--dbe-red-deep)] text-white px-6 py-3.5 rounded-full font-bold uppercase tracking-wider text-xs sm:text-sm shadow-[0_15px_35px_-15px_rgba(227,30,36,0.55)]"
            >
              Get a Free Estimate <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href={`tel:${BRAND.phoneTel}`}
              data-testid="services-index-cta-call"
              className="inline-flex items-center justify-center gap-2 bg-white border-2 border-neutral-900/10 hover:border-[color:var(--dbe-red)]/40 text-neutral-900 px-6 py-3.5 rounded-full font-bebas text-xl sm:text-2xl tracking-wide"
            >
              <Phone className="w-5 h-5 text-[color:var(--dbe-red)]" /> {BRAND.phoneDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="relative py-14 sm:py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SERVICES.map((s) => (
              <Link
                key={s.slug}
                to={`/services/${s.slug}`}
                data-testid={`services-index-card-${s.slug}`}
                className="group relative overflow-hidden rounded-2xl bg-white border border-neutral-200 hover:border-[color:var(--dbe-red)]/60 hover:-translate-y-1 transition-all duration-300 shadow-[0_20px_50px_-30px_rgba(15,15,25,0.15)] hover:shadow-[0_30px_60px_-25px_rgba(227,30,36,0.35)]"
              >
                <div className="relative aspect-[4/3] overflow-hidden bg-neutral-100">
                  <img
                    src={s.img}
                    alt={s.title}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute top-4 left-4 bg-[color:var(--dbe-red)] text-white text-[10px] uppercase tracking-wider font-bold px-3 py-1 rounded-full shadow">
                    {s.tag}
                  </div>
                  <div className="absolute bottom-4 right-4 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center group-hover:bg-[color:var(--dbe-red)] transition-colors">
                    <i className={`fa-solid ${s.icon} text-[color:var(--dbe-red)] group-hover:text-white text-lg transition-colors`} />
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-bebas text-2xl tracking-wide text-neutral-900">{s.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{s.blurb}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[color:var(--dbe-red)] text-xs font-bold uppercase tracking-wider">
                    View Details <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
